import React from "react";
import { FiCheck, FiX } from "react-icons/fi";
import { PackageConfig, Addon } from "@/types";
import { formatCurrency } from "@/utils/format";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import SavingsCalculator from "./SavingsCalculator";

interface PackageComparisonProps {
  packages: PackageConfig[];
  addons: Addon[];
  selectedPackageId?: string;
  onSelectPackage: (packageId: string) => void;
  onClose: () => void;
}

const PackageComparison: React.FC<PackageComparisonProps> = ({
  packages,
  addons,
  selectedPackageId,
  onSelectPackage,
  onClose,
}) => {
  if (packages.length === 0) return null;

  // Collect all unique features across packages
  const allFeatures = new Set<string>();
  packages.forEach((pkg) => {
    pkg.features?.forEach((feature) => allFeatures.add(feature));
    pkg.includedAddons.forEach((addonId) => {
      const addon = addons.find((a) => a.id === addonId);
      if (addon) allFeatures.add(addon.title);
    });
  });

  const featureList = Array.from(allFeatures);

  const hasFeature = (pkg: PackageConfig, feature: string): boolean => {
    const hasInFeatures = pkg.features?.includes(feature);
    const hasInAddons = pkg.includedAddons.some((addonId) => {
      const addon = addons.find((a) => a.id === addonId);
      return addon?.title === feature;
    });
    return hasInFeatures || hasInAddons;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full">
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Compare Packages</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label="Close comparison"
            >
              <FiX size={24} />
            </button>
          </div>

          <div className="p-4 sm:p-6 overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr>
                  <th className="text-left pb-4 pr-4 align-top w-1/4">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Features
                    </span>
                  </th>
                  {packages.map((pkg) => (
                    <th key={pkg.id} className="text-center pb-4 px-2 align-top w-1/4">
                      <div className="space-y-2">
                        {pkg.badge && (
                          <Badge
                            variant={
                              pkg.badge === "popular" || pkg.badge === "best-value"
                                ? "primary"
                                : pkg.badge === "recommended"
                                  ? "success"
                                  : pkg.badge === "limited-time"
                                    ? "warning"
                                    : "default"
                            }
                            size="sm"
                          >
                            {pkg.badgeText || pkg.badge.toUpperCase()}
                          </Badge>
                        )}
                        <h3 className="font-bold text-gray-900 dark:text-white">{pkg.name}</h3>
                        <div className="space-y-1">
                          {pkg.originalPrice && pkg.originalPrice > pkg.price && (
                            <div className="text-sm text-gray-500 line-through">
                              {formatCurrency(pkg.originalPrice)}
                            </div>
                          )}
                          <div className="text-2xl font-bold text-primary-600">
                            {formatCurrency(pkg.price)}
                          </div>
                          <SavingsCalculator
                            originalPrice={pkg.originalPrice}
                            currentPrice={pkg.price}
                            discount={pkg.discount}
                            className="justify-center text-xs"
                          />
                        </div>
                        <Button
                          size="sm"
                          onClick={() => {
                            onSelectPackage(pkg.id);
                            onClose();
                          }}
                          variant={selectedPackageId === pkg.id ? "primary" : "outline"}
                        >
                          {selectedPackageId === pkg.id ? "Selected" : "Select"}
                        </Button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {featureList.map((feature, index) => (
                  <tr key={index} className="border-t border-gray-200 dark:border-gray-700">
                    <td className="py-3 pr-4 text-sm text-gray-900 dark:text-white">{feature}</td>
                    {packages.map((pkg) => (
                      <td key={pkg.id} className="py-3 px-2 text-center">
                        {hasFeature(pkg, feature) ? (
                          <FiCheck className="inline text-green-600" size={20} />
                        ) : (
                          <FiX className="inline text-gray-300" size={20} />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageComparison;
